import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  InjectionToken,
  input,
  output,
  signal,
  Signal,
} from '@angular/core';
import { ButtonModule } from '@openng/optimus-ui/button';
import { MatMenuModule } from '@angular/material/menu';
import { InitialsImagePipe } from '@nx-vigil/core/web/shared/ui';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';

export const CORE_PROFILE_USER = new InjectionToken<Signal<User | undefined>>(
  'CoreUser',
  {
    providedIn: 'root',
    factory: () => signal<User | undefined>(undefined),
  },
);

export const CORE_PROFILE_PORTAL_URL = new InjectionToken<
  Signal<string | undefined>
>('CoreProfilePortalUrl', {
  providedIn: 'root',
  factory: () => signal<string | undefined>(undefined),
});

@Component({
  selector: 'core-profile',
  imports: [
    ButtonModule,
    MatMenuModule,
    InitialsImagePipe,
    ConfirmDialogModule,
  ],
  providers: [ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (resolvedUser(); as user) {
      <div
        class="flex cursor-pointer items-center space-x-2 rounded-sm p-2"
        #popoverTrigger="matMenuTrigger"
        [matMenuTriggerFor]="popover"
        (window:resize)="popoverTrigger.closeMenu()"
        data-testid="profile-button"
      >
        <img
          class="h-6 w-6 rounded-full bg-white"
          [src]="user.pictureUrl || (user.fullName || '' | initialsImage)"
          data-testid="pictureUrl"
          alt="profile"
        />
        <div class="text-primary font-medium">
          <div data-testid="fullName">{{ user.fullName }}</div>
        </div>
      </div>
    } @else {
      <p-button
        (click)="onLogin()"
        [text]="true"
        data-testid="login-button"
        label="Login"
        i18n-label="@@btn-login"
      />
    }
    <mat-menu #popover="matMenu" class="bg-1">
      <div class="p-2">
        @if (resolvedUser(); as user) {
          <div>
            <div data-testid="email" class="text-primary">{{ user.email }}</div>
            @if (user.organization) {
              <div
                data-testid="orgName"
                class="text-sm text-right text-primary"
              >
                {{ user.organization.name }}
              </div>
            }
          </div>
        }
        <hr class="bg-accent mx-auto my-4 h-0.5 w-auto rounded border-0" />
        @if (resolvedUser(); as user) {
          @if (user.organizations && user.organizations.length > 1) {
            <button
              (click)="onSwitchCustomer()"
              mat-menu-item
              data-testid="switch-customer-button"
            >
              <span class="text-primary" i18n="@@btn-switch-customer"
                >Switch Customer</span
              >
            </button>
          }
        }
        @if (resolvedPortalUrl(); as portalUrl) {
          <a
            [href]="portalUrl"
            target="_blank"
            rel="noopener"
            mat-menu-item
            data-testid="product-portal-link"
          >
            <span class="text-primary" i18n="@@btn-product-portal"
              >Product Portal</span
            >
          </a>
        }
        <p-confirmdialog />
        <button (click)="onLogout()" mat-menu-item data-testid="logout-button">
          <span class="text-primary" i18n="@@btn-logout">Logout</span>
        </button>
      </div>
    </mat-menu>
    <ng-template #login>
      <p-button
        (click)="onLogin()"
        [text]="true"
        data-testid="login-button"
        label="Login"
        i18n-label="@@btn-login"
      />
    </ng-template>
  `,
})
export class ProfileComponent {
  private readonly confirmationService = inject(ConfirmationService);

  public user = input<User | undefined>();
  private readonly injectedUser =
    inject<Signal<User | undefined>>(CORE_PROFILE_USER);
  protected resolvedUser = computed(() => this.user() ?? this.injectedUser());

  public portalUrl = input<string | undefined>();
  private readonly injectedPortalUrl = inject<Signal<string | undefined>>(
    CORE_PROFILE_PORTAL_URL,
  );
  protected resolvedPortalUrl = computed(
    () => this.portalUrl() ?? this.injectedPortalUrl(),
  );

  public login = output<void>();

  public logout = output<void>();

  public switchCustomer = output<void>();

  protected onLogout() {
    this.confirmationService.confirm({
      header: 'Confirm Logout',
      message:
        'You will be logged out of all 3xLOGIC products associated with VIGIL Identity. Are you sure you want to continue?',
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: $localize`:@@cancel:Cancel`,
        severity: 'secondary',
      },
      acceptButtonProps: {
        label: $localize`:@@ok:OK`,
      },
      accept: () => {
        this.logout.emit();
      },
    });
  }

  protected onLogin() {
    this.login.emit();
  }

  protected onSwitchCustomer() {
    this.switchCustomer.emit();
  }
}

export interface User {
  fullName?: string;
  email?: string;
  pictureUrl?: string;
  organization?: {
    name: string;
    logoUrl?: string;
  };
  organizations?: string[];
}
